import React, { useEffect, useState } from 'react';

import type { IOQueFalta, IProblema } from '../../../../../hooks/useFechamentoContabilMensal';
import { brl, MODULO } from '../regras';
import { numero } from '../regrasDaCarteira';
import { Paginacao } from './Paginacao';

const POR_PAGINA = 12;
const GRAV: Record<string, [string, string, string]> = {
  critica: ['Crítica', '#FBE9E7', '#B91C1C'],
  alta: ['Alta', '#FCF1DD', '#B45309'],
};
const gravidade = (s: string) => GRAV[s] ?? ['Média', '#F1F5F9', '#334155'];

const TH: React.CSSProperties = { padding: '8px 12px', fontWeight: 600 };

const Selo = ({ severidade }: { severidade: string }) => {
  const [rot, fundo, cor] = gravidade(severidade);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        background: fundo,
        color: cor,
      }}
    >
      {rot}
    </span>
  );
};

export function OQueFalta({
  titulo,
  porEmpresa,
  porProblema,
  onAbrir,
}: {
  titulo: string;
  porEmpresa: { total: number; itens: IOQueFalta[] };
  porProblema: { total: number; itens: IProblema[] };
  onAbrir: (i: IOQueFalta) => void;
}) {
  const [visao, setVisao] = useState<'empresa' | 'problema'>('empresa');
  const [pagina, setPagina] = useState(1);
  useEffect(() => setPagina(1), [porEmpresa, porProblema, visao]);

  const itens = visao === 'empresa' ? porEmpresa.itens : porProblema.itens;
  const paginas = Math.max(1, Math.ceil(itens.length / POR_PAGINA));
  const daPagina = itens.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const vazia = porEmpresa.itens.length === 0;
  const maiorEmp = Math.max(1, ...porProblema.itens.map(p => p.qtd_empresas));

  const aba = (k: 'empresa' | 'problema', rot: string) => {
    const ativo = visao === k;
    return (
      <button
        type="button"
        key={k}
        onClick={() => setVisao(k)}
        aria-pressed={ativo}
        style={{
          height: 30,
          padding: '0 12px',
          border: 0,
          borderRadius: 8,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          background: ativo ? '#FFFFFF' : 'transparent',
          color: ativo ? '#0F172A' : '#52525B',
          boxShadow: ativo ? '0 1px 2px rgba(15, 23, 42, .12)' : 'none',
        }}
      >
        {rot}
      </button>
    );
  };

  let rodape = `${porProblema.total} problema(s) no recorte, dos críticos para os altos`;
  if (visao === 'empresa') {
    rodape = `Mostrando ${daPagina.length} de ${numero(porEmpresa.total)}`;
    if (porEmpresa.total > porEmpresa.itens.length)
      rodape += ` · as ${porEmpresa.itens.length} que mais pesam; use a busca ou os filtros para chegar às demais`;
  }

  return (
    <section
      aria-label="O que falta para liberar"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderRadius: 16,
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>O que falta para liberar</h2>
          <span style={{ fontSize: 12, color: '#52525B' }}>
            Pendências críticas e altas dos meses que ainda não estão livres, no mesmo recorte dos
            cards · {titulo}
          </span>
        </div>
        <div
          role="group"
          aria-label="Agrupar pendências"
          style={{ display: 'flex', gap: 2, padding: 3, background: '#F1F5F9', borderRadius: 10 }}
        >
          {aba('empresa', `Por empresa (${numero(porEmpresa.total)})`)}
          {aba('problema', `Por problema (${numero(porProblema.total)})`)}
        </div>
      </div>

      {vazia && (
        <p
          style={{
            margin: 0,
            padding: 24,
            textAlign: 'center',
            fontSize: 13,
            color: '#52525B',
            background: '#F8FAFC',
            borderRadius: 12,
          }}
        >
          Nenhuma pendência crítica ou alta neste recorte — as empresas selecionadas estão fechadas
          ou liberadas.
        </p>
      )}

      {!vazia && visao === 'empresa' && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 980 }}
          >
            <thead>
              <tr
                style={{
                  textAlign: 'left',
                  color: '#52525B',
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                <th style={{ ...TH, padding: '8px 12px 8px 0' }}>Empresa</th>
                <th style={TH}>Gravidade</th>
                <th style={TH}>O que trava</th>
                <th style={TH}>Onde resolver</th>
                <th style={{ ...TH, textAlign: 'right' }}>Pendências</th>
                <th style={{ ...TH, textAlign: 'right' }}>Meses</th>
                <th style={{ ...TH, padding: '8px 0 8px 12px', textAlign: 'right' }}>
                  Valor envolvido
                </th>
              </tr>
            </thead>
            <tbody>
              {(daPagina as IOQueFalta[]).map(p => (
                <tr
                  key={p.id}
                  className="linha"
                  onClick={() => onAbrir(p)}
                  title="Abrir a empresa na tela onde a pendência se resolve"
                  style={{ borderTop: '1px solid #EEF2F6', cursor: 'pointer' }}
                >
                  <td style={{ padding: '10px 12px 10px 0', maxWidth: 260 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.empresa}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{p.external_id}</div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Selo severidade={p.severidade} />
                  </td>
                  <td style={{ padding: '10px 12px', minWidth: 240 }}>
                    <div style={{ color: '#0F172A' }}>
                      <b style={{ fontWeight: 700 }}>{p.codigo}</b> · {p.titulo}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>
                      {p.vezes}× · {p.distintos} problema(s) distinto(s)
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#334155' }}>
                    {MODULO[p.modulo] ?? p.modulo}
                  </td>
                  <td
                    style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {numero(p.pendencias)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14 }}>
                    {p.meses}
                  </td>
                  <td
                    style={{
                      padding: '10px 0 10px 12px',
                      textAlign: 'right',
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.valor ? brl(p.valor) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!vazia && visao === 'problema' && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 900 }}
          >
            <thead>
              <tr
                style={{
                  textAlign: 'left',
                  color: '#52525B',
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                <th style={{ ...TH, padding: '8px 12px 8px 16px', width: 92 }}>Gravidade</th>
                <th style={TH}>Problema</th>
                <th style={TH}>Onde resolver</th>
                <th style={{ ...TH, width: 190 }}>Empresas</th>
                <th style={{ ...TH, textAlign: 'right' }}>Ocorrências</th>
                <th style={{ ...TH, padding: '8px 16px 8px 12px', textAlign: 'right' }}>
                  Valor envolvido
                </th>
              </tr>
            </thead>
            <tbody>
              {(daPagina as IProblema[]).map(q => (
                <tr
                  key={`${q.codigo}|${q.titulo}`}
                  className="linha"
                  style={{ borderTop: '1px solid #EEF2F6' }}
                >
                  <td style={{ padding: '8px 12px 8px 16px' }}>
                    <Selo severidade={q.severidade} />
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      maxWidth: 420,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: '#0F172A',
                    }}
                    title={q.titulo}
                  >
                    <b style={{ fontWeight: 700 }}>{q.codigo}</b> · {q.titulo}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#334155' }}>
                    {MODULO[q.modulo] ?? q.modulo}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 80,
                          height: 6,
                          borderRadius: 3,
                          background: '#EEF2F6',
                          overflow: 'hidden',
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            height: 6,
                            borderRadius: 3,
                            width: `${Math.max(4, Math.round((q.qtd_empresas / maiorEmp) * 100))}%`,
                            background: q.severidade === 'critica' ? '#DC2626' : '#D97706',
                          }}
                        />
                      </span>
                      <b style={{ minWidth: 36, textAlign: 'right', fontSize: 13 }}>
                        {numero(q.qtd_empresas)}
                      </b>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 14 }}>
                    {numero(q.ocorrencias)}
                  </td>
                  <td
                    style={{
                      padding: '8px 16px 8px 12px',
                      textAlign: 'right',
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {q.valor ? brl(q.valor) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Paginacao rodape={rodape} pagina={pagina} paginas={paginas} onPagina={setPagina} />
    </section>
  );
}
